// src/components/LogsViewer.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
} from "firebase/firestore";
import { db } from "../firebase";
import { buttons, rounded, spacing } from "../styles/tokens";

/** Safely convert our event's timestamp fields to a Date. */
function tsToDate(evt) {
  // Prefer Firestore Timestamp 'ts'
  const t =
    evt?.ts && typeof evt.ts.toDate === "function"
      ? evt.ts.toDate()
      : evt?.__ts_client
      ? new Date(evt.__ts_client)
      : null;
  return t || new Date(0);
}

function fmtDate(d) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(d);
  } catch {
    return d?.toISOString?.() ?? "";
  }
}

function Chevron({ open }) {
  return (
    <span className={`inline-block transition-transform ${open ? "rotate-90" : ""}`}>
      ▸
    </span>
  );
}

export default function LogsViewer() {
  const [rows, setRows] = useState([]); // raw events
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const lastDocRef = useRef(null);

  // Open/close state
  const [openUsers, setOpenUsers] = useState({}); // uid -> bool
  const [openSessions, setOpenSessions] = useState({}); // `${uid}|${sessionId}` -> bool
  const [openEvents, setOpenEvents] = useState({}); // docId -> bool

  // User identity map: uid -> { email, displayName, ... }
  const [userMap, setUserMap] = useState({});

  // Pagination size (tweakable)
  const PAGE_SIZE = 200;

  // Fetch logs page (ordered by ts desc)
  const fetchPage = useCallback(async () => {
    if (loading || done) return;
    setLoading(true);
    try {
      const base = collection(db, "logs");
      const q = lastDocRef.current
        ? query(base, orderBy("ts", "desc"), startAfter(lastDocRef.current), limit(PAGE_SIZE))
        : query(base, orderBy("ts", "desc"), limit(PAGE_SIZE));

      const snap = await getDocs(q);
      if (snap.empty) {
        setDone(true);
        return;
      }

      const batch = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRows((prev) => [...prev, ...batch]);
      lastDocRef.current = snap.docs[snap.docs.length - 1];
    } finally {
      setLoading(false);
    }
  }, [loading, done]);

  // Initial fetch (once)
  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // Fetch users map (uid -> profile) once
  useEffect(() => {
    const run = async () => {
      try {
        const snap = await getDocs(collection(db, "users"));
        const map = {};
        snap.forEach((d) => {
          const data = d.data() || {};
          map[d.id] = {
            email: data.email || "",
            displayName: data.displayName || "",
            photoURL: data.photoURL || "",
          };
        });
        setUserMap(map);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("LogsViewer: failed to load user profiles", err);
      }
    };
    run();
  }, []);

  // Group rows by uid -> sessionId, events sorted desc
  const groups = useMemo(() => {
    const byUser = new Map();
    for (const e of rows) {
      const uid = e.uid || "anonymous";
      const sid = e.sessionId || "no-session";
      if (!byUser.has(uid)) byUser.set(uid, new Map());
      const bySession = byUser.get(uid);
      if (!bySession.has(sid)) bySession.set(sid, []);
      bySession.get(sid).push(e);
    }
    // Sort events DESC in each session
    for (const [, sessions] of byUser) {
      for (const [, evts] of sessions) {
        evts.sort((a, b) => tsToDate(b) - tsToDate(a));
      }
    }
    return byUser;
  }, [rows]);

  const expandAll = () => {
    const newUsers = {};
    const newSessions = {};
    const newEvents = {};
    for (const [uid, sessions] of groups) {
      newUsers[uid] = true;
      for (const [sid, evts] of sessions) {
        newSessions[`${uid}|${sid}`] = true;
        for (const e of evts) newEvents[e.id] = false; // keep events collapsed by default
      }
    }
    setOpenUsers(newUsers);
    setOpenSessions(newSessions);
    setOpenEvents(newEvents);
  };

  const collapseAll = () => {
    setOpenUsers({});
    setOpenSessions({});
    setOpenEvents({});
  };

  const renderUserIdentity = (uid) => {
    const profile = userMap[uid];
    if (!profile) return <code className="text-xs">{uid}</code>;
    const name = profile.displayName || profile.email || uid;
    return (
      <>
        <span className="font-medium">{name}</span>
        {profile.email && profile.displayName ? (
          <span className="text-xs text-gray-500">&nbsp;•&nbsp;{profile.email}</span>
        ) : null}
        {!profile.displayName && !profile.email ? (
          <span className="text-xs text-gray-500">&nbsp;•&nbsp;{uid}</span>
        ) : null}
      </>
    );
  };

  return (
    <div className={`${spacing.md} max-w-5xl mx-auto`}>
      <header className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">Logs Viewer</h1>
        <div className="flex gap-2">
          <button className={buttons.whitePurple} onClick={expandAll}>
            Expand all
          </button>
          <button className={buttons.whitePurple} onClick={collapseAll}>
            Collapse all
          </button>
        </div>
      </header>

      {[...groups.entries()].map(([uid, sessions]) => {
        const userOpen = !!openUsers[uid];
        const total = [...sessions.values()].reduce((acc, a) => acc + a.length, 0);
        return (
          <section key={uid} className="mb-4">
            <button
              onClick={() => setOpenUsers((s) => ({ ...s, [uid]: !userOpen }))}
              className={`w-full text-left bg-white border ${rounded.md} px-3 py-2 hover:bg-gray-50`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Chevron open={userOpen} />
                  <span className="font-medium">User:&nbsp;</span>
                  <span>{renderUserIdentity(uid)}</span>
                </div>
                <span className="text-xs text-gray-500">
                  {total} events • {sessions.size} sessions
                </span>
              </div>
            </button>

            {userOpen && (
              <div className="mt-2 pl-4 border-l">
                {[...sessions.entries()].map(([sid, evts]) => {
                  const key = `${uid}|${sid}`;
                  const open = !!openSessions[key];
                  const newest = evts[0] ? fmtDate(tsToDate(evts[0])) : "";
                  const oldest = evts[evts.length - 1]
                    ? fmtDate(tsToDate(evts[evts.length - 1]))
                    : "";
                  return (
                    <div key={key} className="mb-3">
                      <button
                        onClick={() =>
                          setOpenSessions((s) => ({ ...s, [key]: !open }))
                        }
                        className={`w-full text-left bg-gray-50 border ${rounded.md} px-3 py-2 hover:bg-gray-100`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Chevron open={open} />
                            <span className="font-medium">Session:&nbsp;</span>
                            <code className="text-xs">{sid}</code>
                          </div>
                          <span className="text-xs text-gray-500">
                            {evts.length} events • {oldest} → {newest}
                          </span>
                        </div>
                      </button>

                      {open && (
                        <ol className="mt-2 ml-4 border-l">
                          {evts.map((evt) => {
                            const openEvt = !!openEvents[evt.id];
                            const when = fmtDate(tsToDate(evt));
                            return (
                              <li key={evt.id} className="relative pl-4 py-2">
                                <span className="absolute -left-1 top-3 w-2 h-2 rounded-full bg-purple-500" />
                                <button
                                  onClick={() =>
                                    setOpenEvents((s) => ({
                                      ...s,
                                      [evt.id]: !openEvt,
                                    }))
                                  }
                                  className="w-full text-left"
                                  title="Click to expand details"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Chevron open={openEvt} />
                                      <span className="font-medium">
                                        {evt.type || "(no type)"}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {when}
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {evt.data?.videoId
                                        ? `video:${evt.data.videoId}`
                                        : ""}
                                      {evt.data?.sectionId
                                        ? `  section:${evt.data.sectionId}`
                                        : ""}
                                    </div>
                                  </div>
                                </button>

                                {openEvt && (
                                  <div className={`mt-2 bg-white border ${rounded.md} p-3`}>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                      <div>
                                        <span className="text-gray-500">Doc ID:</span>{" "}
                                        <code className="text-xs">{evt.id}</code>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">App:</span>{" "}
                                        <code className="text-xs">{evt.appVersion || "-"}</code>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Session:</span>{" "}
                                        <code className="text-xs">{evt.sessionId || "-"}</code>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">User Agent:</span>{" "}
                                        <span className="text-xs break-all">{evt.ua || "-"}</span>
                                      </div>
                                    </div>
                                    <div className="mt-3">
                                      <div className="text-gray-500 text-sm mb-1">
                                        Payload
                                      </div>
                                      <pre
                                        className={`text-xs overflow-auto p-2 bg-gray-50 ${rounded.md}`}
                                        style={{ maxHeight: 260 }}
                                      >
{JSON.stringify(
  {
    type: evt.type,
    uid: evt.uid,
    user: userMap[evt.uid]
      ? {
          email: userMap[evt.uid].email,
          displayName: userMap[evt.uid].displayName,
        }
      : undefined,
    sessionId: evt.sessionId,
    ts: tsToDate(evt).toISOString(),
    data: evt.data,
  },
  null,
  2
)}
                                      </pre>
                                    </div>
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ol>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}

      <div className="flex items-center justify-center my-6">
        {!done ? (
          <button disabled={loading} onClick={fetchPage} className={buttons.whitePurple}>
            {loading ? "Loading…" : "Load more"}
          </button>
        ) : (
          <div className="text-sm text-gray-500">No more logs.</div>
        )}
      </div>
    </div>
  );
}
